import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import config from "../config/config";

const SuperAdminUserManagement = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
    permissions: [],
    storeId: "",
  });

  const roles = ["super_manage", "admin", "staff"];
  const availablePermissions = [
    "product:read",
    "product:create",
    "product:update",
    "product:delete",
    "order:read",
    "order:create",
    "order:update",
    "order:delete",
    "customer:read",
    "customer:create",
    "customer:update",
    "customer:delete",
    "category:read",
    "category:create",
    "category:update",
    "category:delete",
    "user:manage",
    "user:super_manage",
  ];

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(
        `${config.backendUrl}/api/users/super-admin/users`,
        {
          headers: { "x-auth-token": token },
        }
      );
      setUsers(res.data);
      setLoading(false);
    } catch (error) {
      showToast("Error fetching users", "error");
      console.error(error);
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setCurrentUser(user);
    if (user) {
      setForm({
        name: user.name,
        email: user.email,
        password: "", // Never pre-fill password
        role: user.role,
        permissions: user.permissions || [],
        storeId: user.storeId || "",
      });
    } else {
      setForm({
        name: "",
        email: "",
        password: "",
        role: "staff",
        permissions: [],
        storeId: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser(null);
    setForm({
      name: "",
      email: "",
      password: "",
      role: "staff",
      permissions: [],
      storeId: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handlePermissionsChange = (e) => {
    const { value } = e.target;
    setForm({
      ...form,
      permissions: typeof value === "string" ? value.split(",") : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentUser) {
        // Update user
        await axios.put(
          `${config.backendUrl}/api/users/super-admin/users/${currentUser._id}`,
          form,
          {
            headers: { "x-auth-token": token },
          }
        );
        showToast("User updated successfully", "success");
      } else {
        // Create user
        await axios.post(
          `${config.backendUrl}/api/users/super-admin/users`,
          form,
          {
            headers: { "x-auth-token": token },
          }
        );
        showToast("User created successfully", "success");
      }
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      showToast(error.response?.data?.message || "Error saving user", "error");
      console.error(error);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(
          `${config.backendUrl}/api/users/super-admin/users/${userId}`,
          {
            headers: { "x-auth-token": token },
          }
        );
        showToast("User deleted successfully", "success");
        fetchUsers();
      } catch (error) {
        showToast(
          error.response?.data?.message || "Error deleting user",
          "error"
        );
        console.error(error);
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Super Admin User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ mb: 2 }}
        >
          Add New User
        </Button>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Store ID</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.storeId}</TableCell>
                  <TableCell>{user.permissions.join(", ")}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(user._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            {currentUser ? "Edit User" : "Add New User"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Name"
              type="text"
              fullWidth
              variant="standard"
              value={form.name}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="email"
              label="Email"
              type="email"
              fullWidth
              variant="standard"
              value={form.email}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="password"
              label="Password (leave blank to keep current)"
              type="password"
              fullWidth
              variant="standard"
              value={form.password}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense" variant="standard">
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={form.role}
                onChange={handleChange}
                label="Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" variant="standard">
              <InputLabel>Permissions</InputLabel>
              <Select
                name="permissions"
                multiple
                value={form.permissions}
                onChange={handlePermissionsChange}
                label="Permissions"
                renderValue={(selected) => selected.join(", ")}
              >
                {availablePermissions.map((permission) => (
                  <MenuItem key={permission} value={permission}>
                    {permission}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="storeId"
              label="Store ID (for child accounts or new stores)"
              type="text"
              fullWidth
              variant="standard"
              value={form.storeId}
              onChange={handleChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {currentUser ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SuperAdminUserManagement;
